import axios from "axios";
import { API_URL, emailValidationRegex } from "const/common";
import Image from "next/image";
import { useState } from "react";
import { useForm } from "react-hook-form";
import Container from "../common/Container";
import GetResume from "./common/GetResume";
import Icon from "./Icon";

const Hero = ({ data }) => {
  const { headLine, description, featurePoints, formData, button, techName } =
    data;
  const [showMessage, setShowMessage] = useState("");
  const [disabled, setDisabled] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const onSubmit = (values) => {
    if (errors && Object.keys(errors).length === 0) {
      setDisabled(true);
      axios
        .post(`${API_URL}tech/add-enquiry`, {
          ...values,
          techName,
        })
        .then(() => {
          setShowMessage(true);
          reset();
        })
        .catch(() => {
          console.error("Error making API request:");
        })
        .finally(() => {
          setDisabled(false);
        });
    }
  };

  return (
    <Container
      large
      className="bg-header-background bg-cover bg-bottom bg-no-repeat"
    >
      <div className="flex flex-col xl:flex-row justify-content-between pt-40 pb-20">
        <div className="w-full xl:w-7/12">
          <h1 className="leading-snug text-4xl md:text-4xl lg:text-6xl tracking-wide font-medium text-white m-0 pb-8 md:pb-4 lg:pb-8">
            <span>{headLine}</span>
          </h1>
          <div
            className="text-justify m-0 text-white text-base [&>p]:mb-4 lg:pr-20 pb-6 lg:pb-4 [&_a]:text-blue-500"
            dangerouslySetInnerHTML={{ __html: description }}
          ></div>
          <div className="grid gap-y-8 grid-row-4 md:grid-rows-none md:grid-cols-2 pt-2 pb-10 md:py-14 lg:pt-20 lg:pb-14">
            {featurePoints.data.map((item) => (
              <div key={item.title} className="flex">
                <div>
                  <Icon color={featurePoints.icon.color} />
                </div>
                <div className="pl-8 md:pl-4 lg:pl-8">
                  <p className="text-base m-0 text-white">
                    <span>{item.title}</span>
                  </p>
                  <span className="text-base m-0 text-white">
                    {item.subTitle}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="w-full xl:w-5/12 mt-4">
          <div
            className="relative rounded-3xl md:mx-auto xl:mr-0 xl:ml-auto md:w-[425px] flex justify-center items-center p-px lg:py-px lg:px-0"
            style={{ background: formData.borderColor }}
          >
            <div className="rounded-3xl p-8 md:py-6 md:px-8 w-[-webkit-fill-available] md:w-[423px] bg-primary-black">
              <div className="pb-4">
                <span
                  className="text-4xl md:text-7xl fw-bold text-transparent bg-clip-text leading-tight"
                  style={{
                    backgroundImage: formData.countColor,
                  }}
                >
                  {formData.devWehave}
                </span>
                <span
                  className="text-transparent bg-clip-text font-medium text-3xl md:text-2xl"
                  style={{ backgroundImage: formData.countColor }}
                >
                  /{formData.totalDev}
                </span>
                <span className="font-light text-white text-base ps-2 whitespace-nowrap">
                  Developers are Available for
                </span>
              </div>
              <div className="flex items-center justify-between lg:justify-start pb-2 h-14">
                <div>
                  <h2 className="text-5xl md:text-7xl font-bold text-white m-0 pr-8">
                    <span>Hiring</span>
                  </h2>
                </div>
                <div className="flex mt-4">
                  <div>
                    <Image
                      alt="developer"
                      height={30}
                      quality={100}
                      src="/assets/technologies/developer-1.webp"
                      width={30}
                    />
                  </div>
                  <div className="-ml-2">
                    <Image
                      alt="developer"
                      height={30}
                      quality={100}
                      src="/assets/technologies/developer-2.webp"
                      width={30}
                    />
                  </div>
                  <div className="-ml-2">
                    <Image
                      alt="developer"
                      height={30}
                      quality={100}
                      src="/assets/technologies/developer-3.webp"
                      width={30}
                    />
                  </div>
                </div>
              </div>
              <p className="text-white text-xl my-8">
                <span>Let’s Get Started in</span>
                <span className="text-primary-yellow font-semibold">
                  &nbsp;1 Business Day!
                </span>
              </p>
              <form className="pb-6" onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-6">
                  <input
                    className="w-full text-base font-normal border-b border-solid border-white/25 bg-transparent text-white/25 focus-visible:outline-none"
                    {...register("email", {
                      required: "Kindly enter valid Email address.",
                      pattern: {
                        value: emailValidationRegex,
                        message: "Kindly enter valid Email address.",
                      },
                    })}
                    placeholder="Enter Email ID"
                    type="text"
                  />
                  {errors.email && (
                    <p className="m-0 ml-0 mt-1 text-red-600 text-base">
                      <span>{errors?.email?.message}</span>
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <input
                    id="numOfDevs"
                    {...register("numOfDevs", {
                      min: { value: 1, message: "Min 1" },
                      max: { value: formData.devWehave, message: "Max 10" },
                      required: true,
                    })}
                    className="w-full text-base font-normal border-b border-solid border-white/35 bg-transparent text-white/20 focus-visible:outline-none"
                    placeholder="No. of Developers Required"
                    type="number"
                  />
                  {errors.numOfDevs && (
                    <p className="m-0 ml-0 mt-1 text-red-600 text-base">
                      <span>
                        Kindly select the value from pool of available
                        Resources.
                      </span>
                    </p>
                  )}
                </div>
                <div className="text-center mt-12">
                  <GetResume
                    disabled={disabled}
                    button={button}
                    buttonName="Get Resume Now"
                    onClick={onSubmit}
                  />
                </div>
                {showMessage ? (
                  <div className="flex items-center pt-12">
                    <div>
                      <Image
                        alt="Success"
                        height={46.71}
                        quality={100}
                        src="/assets/technologies/success.webp"
                        width={48}
                      />
                    </div>
                    <p className="text-white mb-0 text-base ml-4">
                      <span>
                        Thanks for connecting. Kindly check your inbox for
                        further process.
                      </span>
                    </p>
                  </div>
                ) : undefined}
              </form>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default Hero;
